import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAtomValue } from 'jotai';
import MainURL from '../../../../MainURL';
import { loginStateAtom, userDataAtom } from '../../../../AtomStore';
import { normalizeUserType } from '../../../login/userTypeUtils';
import type { UserTypeValue } from '../../../login/userTypeUtils';

// GET /resume/getresumelist/:userAccount 응답 아이템
export interface MyResumeItem {
  id: string;
  title: string;
  updatedAt: string;
  isPublic: boolean | number;
  hasSelfIntroduction?: boolean;
  portfolioCount?: number;
}

// 사역자정보 열람 권한 — 서버(/resume/ministerdetail)와 같은 기준
//  - 교회 담당자: 로그인 / 사역자: 로그인 + 본인 이력서 작성
export type MinisterBlockReason = 'login' | 'resume' | 'userType';

export interface MinisterAccess {
  loading: boolean;
  canView: boolean;
  blockReason: MinisterBlockReason | null;
  userType: UserTypeValue;
  resumeList: MyResumeItem[];
}

export function useMinisterAccess(): MinisterAccess {
  const isLogin = useAtomValue(loginStateAtom);
  const userData = useAtomValue(userDataAtom);
  const userType = normalizeUserType(userData.userType);
  // 로그인한 회원은 모두 본인 이력서 목록을 확인 (목록 항목 클릭 시 작성 여부 체크에도 사용)
  const shouldFetch = isLogin && !!userData.userAccount;

  const [resumeList, setResumeList] = useState<MyResumeItem[]>([]);
  const [loading, setLoading] = useState(shouldFetch);

  useEffect(() => {
    if (!shouldFetch) {
      setResumeList([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    axios.get(`${MainURL}/resume/getresumelist/${userData.userAccount}`)
      .then((res) => {
        setResumeList(res.data.success && Array.isArray(res.data.resumeList) ? res.data.resumeList : []);
      })
      .catch((err) => {
        console.error('내 이력서 목록 조회 실패:', err);
        setResumeList([]);
      })
      .finally(() => setLoading(false));
  }, [shouldFetch, userData.userAccount]);

  let blockReason: MinisterBlockReason | null = null;
  if (!isLogin) blockReason = 'login';
  else if (userType === 'MINISTER') blockReason = resumeList.length > 0 ? null : 'resume';
  else if (userType !== 'CHURCH') blockReason = 'userType';

  return {
    loading,
    canView: !loading && blockReason === null,
    blockReason: loading ? null : blockReason,
    userType,
    resumeList,
  };
}
